import React, { useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Link, useHistory, useLocation } from "react-router-dom";
import { completeCongresoDelegadoSetup } from "../../../../api/auth";
import { alertError, alertSuccess } from "../../../../utils/logger";

const initialValues = {
  password: "",
  confirmpassword: "",
};

function useInviteToken() {
  const { search } = useLocation();
  return useMemo(() => {
    const q = new URLSearchParams(search);
    return (q.get("token") || "").trim();
  }, [search]);
}

export default function ActivarCongresoDelegado() {
  const history = useHistory();
  const token = useInviteToken();

  const ActivarSchema = Yup.object().shape({
    password: Yup.string()
      .min(6, "Mínimo 6 caracteres")
      .max(128, "Máximo 128 caracteres")
      .required("Indica una contraseña"),
    confirmpassword: Yup.string()
      .required("Confirma la contraseña")
      .oneOf([Yup.ref("password")], "Las contraseñas no coinciden"),
  });

  const formik = useFormik({
    initialValues,
    validationSchema: ActivarSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      if (!token) {
        setStatus("Falta el enlace completo del correo (token).");
        setSubmitting(false);
        return;
      }
      try {
        await completeCongresoDelegadoSetup({
          token,
          password: values.password,
        });
        alertSuccess({
          title: "Listo",
          customMessage:
            "Contraseña guardada. Inicia sesión en el backoffice con tu correo y la nueva contraseña.",
        });
        history.push("/auth/login");
      } catch (error) {
        const msg =
          error?.response?.data?.message ||
          "No se pudo activar la cuenta. El enlace puede haber caducado.";
        setStatus(msg);
        alertError({ error, customMessage: msg });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const getInputClasses = (fieldname) => {
    if (formik.touched[fieldname] && formik.errors[fieldname]) return "is-invalid";
    if (formik.touched[fieldname] && !formik.errors[fieldname]) return "is-valid";
    return "";
  };

  return (
    <div>
      <div className="login-form login-forgot" style={{ display: "block" }}>
        <div className="text-center mb-10 mb-lg-20">
          <h3 className="font-size-h1">Activar acceso a congresos</h3>
          <div className="text-muted font-weight-bold">
            Define tu contraseña para el backoffice ACTEDI (solo tú la conocerás).
          </div>
        </div>
        {!token ? (
          <div className="mb-10 alert alert-custom alert-light-danger">
            <div className="alert-text font-weight-bold">
              Abre este formulario desde el enlace que recibiste por correo. Si el enlace
              está incompleto, solicita una nueva invitación al administrador.
            </div>
          </div>
        ) : null}
        <form
          onSubmit={formik.handleSubmit}
          className="form fv-plugins-bootstrap fv-plugins-framework animated animate__animated animate__backInUp"
        >
          {formik.status ? (
            <div className="mb-10 alert alert-custom alert-light-danger alert-dismissible">
              <div className="alert-text font-weight-bold">{formik.status}</div>
            </div>
          ) : null}

          <div className="form-group fv-plugins-icon-container">
            <label className="font-size-h6 font-weight-bolder text-dark">Nueva contraseña</label>
            <input
              type="password"
              autoComplete="new-password"
              className={`form-control form-control-solid h-auto py-7 px-6 rounded-lg ${getInputClasses(
                "password",
              )}`}
              name="password"
              {...formik.getFieldProps("password")}
            />
            {formik.touched.password && formik.errors.password ? (
              <div className="fv-plugins-message-container">
                <div className="fv-help-block">{formik.errors.password}</div>
              </div>
            ) : null}
          </div>

          <div className="form-group fv-plugins-icon-container">
            <label className="font-size-h6 font-weight-bolder text-dark">Repetir contraseña</label>
            <input
              type="password"
              autoComplete="new-password"
              className={`form-control form-control-solid h-auto py-7 px-6 rounded-lg ${getInputClasses(
                "confirmpassword",
              )}`}
              name="confirmpassword"
              {...formik.getFieldProps("confirmpassword")}
            />
            {formik.touched.confirmpassword && formik.errors.confirmpassword ? (
              <div className="fv-plugins-message-container">
                <div className="fv-help-block">{formik.errors.confirmpassword}</div>
              </div>
            ) : null}
          </div>

          <div className="form-group d-flex flex-wrap justify-content-between align-items-center">
            <button
              type="submit"
              disabled={formik.isSubmitting || !token}
              className="btn btn-primary font-weight-bolder font-size-h6 px-8 py-4 my-3 mr-2"
            >
              Guardar contraseña
            </button>
            <Link
              to="/auth/login"
              className="btn btn-light-primary font-weight-bolder font-size-h6 px-8 py-4 my-3"
            >
              Ir al inicio de sesión
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
